import { Interval } from "./interval.tools";
import { formatMinutesHHMM, MINUTES_IN_ONE_DAY, parseMinutesHHMM } from "./time.tools";

const INTERVAL = {
    SPLITTER: ";",
    PORTION_SPLITTER: "-"
};

interface ParseIntervalsOptions {
    /** @returns the parsed number on parse fails can return null or throw an exception */
    parseToken(token: string): number | null;
    /** @returns an Interval if from and to are valid, null otherwise */
    createInterval(from: number | null, to: number | null): Interval | null;
}

export const integerParseOptions: ParseIntervalsOptions = {
    parseToken: (token) => {
        return parseInt(token);
    },
    createInterval: (from, to) => {
        if (from == null || isNaN(from) || to == null || isNaN(to)) { return null; }
        return { from, to };
    }
};

interface StringifyIntervalsOptions {
    /** @returns the token as string */
    stringifyToken(token: number): string;
}

export const integerStringifyOptions: StringifyIntervalsOptions = {
    stringifyToken: (token: number) => String(token)
};

/**
 * @param strIntervals Interval string to parse : Eg : 6-14-22-6;7-15 => [{6;14},{14;22},{22;6},{7;15}]
 * @param p ParseIntervalsOptions (parseToken and createInterval)
 * @returns null if interval is invalid, empty array if string is empty or not defined, the interval array otherwise
 */
export function parseIntervals(
    strIntervals: string | null | undefined,
    p: ParseIntervalsOptions = integerParseOptions
): Interval[] | null {
    if (strIntervals == null || strIntervals === "") {
        return [];
    }
    strIntervals = strIntervals.trim();
    if (strIntervals === "") { return null; }// only spaces => error

    const parsedIntervals: Interval[] = [];
    // Split unrelated intervals : "6-14;7-15" => ["6-14","7-15"]
    const unrelatedIntervals = strIntervals.split(INTERVAL.SPLITTER);
    for (const unrelatedInterval of unrelatedIntervals) {
        // Split related intervals : "6-14-22-6" => ["6","14","22","6"]
        const intervalPortions = unrelatedInterval.split(INTERVAL.PORTION_SPLITTER);
        if (intervalPortions.length <= 1) {
            return null;
        }
        for (let index = 0; index < intervalPortions.length - 1; index++) {
            const intervalPortion = intervalPortions[index];
            const nextIntervalPortion = intervalPortions[index + 1];
            try {
                // Parse intervals : ["6","14","22","6"] => [{360;840},{840;1320},{1320;1800}]
                const from = p.parseToken(intervalPortion);
                const to = p.parseToken(nextIntervalPortion);
                const interval = p.createInterval(from, to);
                if (interval == null) {
                    // parse Error
                    return null;
                }
                parsedIntervals.push(interval);
            } catch {
                // parse Error
                return null;
            }
        }
    }
    return parsedIntervals;
}


/**
 * Intervals to string :[{6;14},{14;22},{22;6},{7;15}] => 6-14-22-6;7-15
 * ! intervals are not meant to contain negative values (it is not handled and there is no error)
 */
export function stringifyIntervals(intervals: Interval[] | null | undefined, p: StringifyIntervalsOptions = integerStringifyOptions): string {

    if (intervals == null) { return ""; }
    let result = "";
    let previousTo: number | undefined = undefined;
    for (const i of intervals) {
        if (previousTo != null) {
            if (previousTo !== i.from) {
                // disconnected interval
                result += INTERVAL.SPLITTER + p.stringifyToken(i.from) + INTERVAL.PORTION_SPLITTER;
            } else {
                // interval is contiguous, do not repeat from
                result += INTERVAL.PORTION_SPLITTER;
            }
        } else {
            // first interval
            result += p.stringifyToken(i.from) + INTERVAL.PORTION_SPLITTER;
        }
        result += p.stringifyToken(i.to);
        previousTo = i.to;
    }
    return result;
}

/**
 * @param data Intervals string to parse : Eg : 6-14-22-6;7-15,6-14 => [[{360;840},{840;1320},{1320;1800},{420;900}],[{420;900}]]
 * @param options
 * @param options.strict Parse only valid string => will throw an error if the string has a negative factor and/or if HH>=24 or mm>=60
 * @returns null if any interval is invalid, empty array if string is empty or not defined, the intervals array otherwise
 */
export function parseMinutesIntervalsArray(data: string | null | undefined, options?: { strict?: boolean }): Interval[][] | null {
    if (!data) { return []; }
    const intervals = data.split(",");
    const result: Interval[][] = [];
    for (const s of intervals) {
        const parsed = parseMinutesIntervals(s, options);
        if (parsed == null) {
            // error
            return null;
        }
        result.push(parsed);
    }
    return result;
}

/**
 * @param strIntervals Interval string to parse : Eg : 6-14-22-6;7-15 => [{360;840},{840;1320},{1320;1800},{420;900}]
 * @param options
 * @param options.strict Parse only valid string => will throw an error if the string has a negative factor and/or if HH>=24 or mm>=60
 * @returns null if interval is invalid, empty array if string is empty or not defined, the interval array otherwise
 */
export function parseMinutesIntervals(strIntervals: string | null | undefined, options?: { strict?: boolean }): Interval[] | null {
    const minutesParseOptions: ParseIntervalsOptions = {
        parseToken: (token) => {
            return parseMinutesHHMM(token, options);
        },
        createInterval: (from, to) => {
            if (from == null || to == null) { return null; }
            if (to < from) {
                to += MINUTES_IN_ONE_DAY;
            }
            return { from, to };
        }
    };
    return parseIntervals(strIntervals, minutesParseOptions);
}

/**
 * Format a string interval : 6-14-22-6 => 06:00-14:00-22:00-06:00, 6-14;7-15 => 06:00-14:00;07:00-15:00
 * @param intervalsStr string to reformat
 * @param options options
 * @param options.dayFormat convert only for 24h "day" duration: formatMinutesHHMM(1520) => "25:20" / formatMinutesHHMM(1520, {dayFormat: true}) => "01:20"
 * @returns "" if intervalsStr is not defined, the original intervalsStr if it contains an error, the formatted string otherwise
 */
export function reformatMinutesIntervalsString(intervalsStr: string | null | undefined, options?: { dayFormat: boolean }): string {
    const intervals = parseMinutesIntervals(intervalsStr);
    if (intervals == null) {
        // not valid => leave unchanged
        return intervalsStr ?? "";
    }
    return stringifyMinutesIntervals(intervals, options);
}

/**
 * Format a string interval array : 6-14-22-6,6-14;7-15 => 06:00-14:00-22:00-06:00,06:00-14:00;07:00-15:00
 * @param intervalsStr string to reformat
 * @param options options
 * @param options.dayFormat convert only for 24h "day" duration: formatMinutesHHMM(1520) => "25:20" / formatMinutesHHMM(1520, {dayFormat: true}) => "01:20"
 * @returns "" if intervalsStr is not defined, the original intervalsStr if it contains an error, the formatted string otherwise
 */
export function reformatMinutesIntervalsArrayString(intervalsStr: string | null | undefined, options?: { dayFormat: boolean }): string {
    const intervals = parseMinutesIntervalsArray(intervalsStr);
    if (intervals == null) {
        // not valid => leave unchanged
        return intervalsStr ?? "";
    }
    return stringifyMinutesIntervalsArray(intervals, options);
}

/**
 * Intervals to string :[{360;840},{840;1320},{1320;1800},{420;900}] => 6-14-22-6;7-15 06:00-14:00-22:00-06:00;07:00-15:00
 * @param intervals intervals to stringify
 * @param options options
 * @param options.dayFormat convert only for 24h "day" duration: formatMinutesHHMM(1520) => "25:20" / formatMinutesHHMM(1520, {dayFormat: true}) => "01:20"
 * ! intervals are not meant to contain negative values (it is not handled and there is no error)
 */
export function stringifyMinutesIntervals(intervals: Interval[] | null | undefined, options?: { dayFormat: boolean }): string {
    const stringifyMinutesIntervalsOptions: StringifyIntervalsOptions = {
        stringifyToken: (token) => {
            return formatMinutesHHMM(token, options);
        }
    };
    return stringifyIntervals(intervals, stringifyMinutesIntervalsOptions);
}


/**
 * Intervals array to string :[[{360;840},{840;1320},{1320;1800},{420;900}],[{420;900}]] => 6-14-22-6;7-15 06:00-14:00-22:00-06:00;07:00-15:00,07:00-15:00
 * @param obj intervals to stringify
 * @param options options
 * @param options.dayFormat convert only for 24h "day" duration: formatMinutesHHMM(1520) => "25:20" / formatMinutesHHMM(1520, {dayFormat: true}) => "01:20"
 * ! intervals are not meant to contain negative values (it is not handled and there is no error)
 */
export function stringifyMinutesIntervalsArray(obj: Interval[][] | null | undefined, options?: { dayFormat: boolean }): string {
    if (obj != null) {
        return obj.map(intervals => stringifyMinutesIntervals(intervals, options)).join(",");
    }
    return "";
}

